import React, { useRef, useEffect, useState } from "react";
import ImageGallery from "react-image-gallery";
import InnerImageZoom from "react-inner-image-zoom";
import "react-image-gallery/styles/css/image-gallery.css";
import "react-inner-image-zoom/lib/InnerImageZoom/styles.css";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const customStyles = `
  .image-gallery-thumbnails-wrapper.left {
    width: 80px !important;
  }
  
  .image-gallery-thumbnails-wrapper.left .image-gallery-thumbnails {
    position: relative;
    height: 100%;
    overflow-y: auto !important;
    overflow-x: hidden;
    scrollbar-width: none;
    -ms-overflow-style: none;
    touch-action: pan-y !important;
  }
  
  /* Mobile responsive adjustments */
  @media (max-width: 768px) {
    .image-gallery-thumbnails-wrapper.left {
      width: 60px !important;
    }
    
    .image-gallery {
      max-height: 400px !important;
    }
    
    .image-gallery-content {
      max-height: 400px !important;
    }
    
    .image-gallery-slide-wrapper {
      max-height: 400px !important;
    }
    
    .image-gallery-slide {
      max-height: 400px !important;
    }
    
    .image-gallery-image {
      object-fit: contain !important;
      max-height: 400px !important;
      width: 100% !important;
      height: auto !important;
    }
  }
  
  .image-gallery-thumbnails-wrapper.left .image-gallery-thumbnails::-webkit-scrollbar {
    display: none;
  }

  /* Ensure images fit properly in all views */
  .image-gallery-image {
    object-fit: contain !important;
    width: 100% !important;
    height: auto !important;
  }
  
  .image-gallery-slide .image-gallery-image {
    max-width: 100% !important;
    max-height: 100% !important;
  }

  /* Style for thumbnail navigation buttons */
  .thumbnail-nav-button {
    position: absolute;
    z-index: 5;
    background-color: rgba(255, 255, 255, 0.8) !important;
    border: 1px solid rgba(0, 0, 0, 0.1) !important;
    transform: translateX(-50%);
    left: 40px !important;
    width: 24px;
    height: 24px;
  }
  
  @media (max-width: 768px) {
    .thumbnail-nav-button {
      left: 30px !important;
      width: 20px;
      height: 20px;
    }
  }
  
  .up-button {
    top: 10px;
  }
  
  .down-button {
    bottom: 10px;
  }
  
  /* Highlight the active thumbnail with a border */
  .image-gallery-thumbnail.active {
    border: 2px solid #007fff !important;
  }
  
  /* Tight thumbnails display */
  .image-gallery-thumbnails-container {
    cursor: grab;
    display: flex;
    flex-direction: column;
    padding-top: 30px !important; 
    padding-bottom: 30px !important;
  }
  
  @media (max-width: 768px) {
    .image-gallery-thumbnails-container {
      padding-top: 25px !important; 
      padding-bottom: 25px !important;
    }
  }
  
  /* Remove any space between thumbnails */
  .image-gallery-thumbnail {
    margin: 0 !important;
    padding: 0 !important;
    border: none;
    touch-action: pan-y !important;
    cursor: pointer;
    height: auto !important;
  }
  
  /* Make the thumbnails wrap around - key for solving white space issue */
  .image-gallery-thumbnails-container {
    scroll-snap-type: y mandatory;
  }
  
  .image-gallery-thumbnail {
    scroll-snap-align: start;
  }
`;

const ProductCarousel = ({ media, activeImage, setActiveImage }) => {
  const galleryRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Function to handle thumbnail click
  const handleThumbnailClick = (index) => {
    if (galleryRef.current) {
      galleryRef.current.slideToIndex(index);
    }
  };

  // Jump directly to first image when at the last image
  const scrollThumbnailsDown = () => {
    if (currentIndex === media.length - 1) {
      if (galleryRef.current) {
        galleryRef.current.slideToIndex(0);
      }
    } else if (galleryRef.current) {
      galleryRef.current.slideToIndex(currentIndex + 1);
    }
  };

  // Jump directly to last image when at the first image
  const scrollThumbnailsUp = () => {
    if (currentIndex === 0) {
      if (galleryRef.current) {
        galleryRef.current.slideToIndex(media.length - 1);
      }
    } else if (galleryRef.current) {
      galleryRef.current.slideToIndex(currentIndex - 1);
    }
  };

  // Handle slide changes
  const handleSlide = (index) => {
    setCurrentIndex(index);
    setActiveImage(media[index].Media_File_Path);

    // Scroll to make the active thumbnail visible
    const thumbnailsContainer = document.querySelector(
      ".image-gallery-thumbnails"
    );
    const thumbnails = document.querySelectorAll(".image-gallery-thumbnail");

    if (thumbnails && thumbnails[index] && thumbnailsContainer) {
      // Calculate where to scroll
      const thumbnailTop = thumbnails[index].offsetTop;
      const containerHeight = thumbnailsContainer.clientHeight;
      const thumbnailHeight = thumbnails[index].clientHeight;

      // Center the thumbnail in the container
      thumbnailsContainer.scrollTop =
        thumbnailTop - containerHeight / 2 + thumbnailHeight / 2;
    }
  };

  // Handle thumbnail mouse wheel scrolling
  useEffect(() => {
    const handleWheel = (e) => {
      if (e.deltaY > 0) {
        // Scrolling down - check if we're near the end
        const container = document.querySelector(".image-gallery-thumbnails");
        const maxScroll = container.scrollHeight - container.clientHeight;

        if (container.scrollTop >= maxScroll - 20) {
          // We're near the end, move to the first image
          if (galleryRef.current) {
            galleryRef.current.slideToIndex(0);
          }
        }
      }
    };

    const thumbnailsContainer = document.querySelector(
      ".image-gallery-thumbnails"
    );
    if (thumbnailsContainer) {
      thumbnailsContainer.addEventListener("wheel", handleWheel);
    }

    return () => {
      if (thumbnailsContainer) {
        thumbnailsContainer.removeEventListener("wheel", handleWheel);
      }
    };
  }, []);

  useEffect(() => {
    if (activeImage && galleryRef.current) {
      const activeIndex = media.findIndex(
        (mediaItem) => mediaItem.Media_File_Path === activeImage
      );
      if (activeIndex !== -1) {
        galleryRef.current.slideToIndex(activeIndex);
        setCurrentIndex(activeIndex);
      }
    }
  }, [activeImage, media]);

  // Helper to detect if a media item is a video
  const isVideo = (mediaItem) => {
    if (mediaItem.Media_File_Type) {
      return mediaItem.Media_File_Type.toLowerCase().includes("video");
    }
    const ext = mediaItem.Media_File_Path.split(".").pop().toLowerCase();
    return ["mp4", "webm", "ogg", "mov"].includes(ext);
  };

  const images = media.map((mediaItem, index) => ({
    original: process.env.BASEURL + mediaItem.Media_File_Path,
    thumbnail: process.env.BASEURL + mediaItem.Media_File_Path,
    thumbnailLabel: `Media ${index + 1}`,
    originalAlt: `Product media ${index + 1}`,
    thumbnailAlt: `Product thumbnail ${index + 1}`,
    isVideo: isVideo(mediaItem),
    mediaItem,
  }));

  const renderItem = (item) => {
    if (item.isVideo) {
      return (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <video
            src={item.original}
            controls
            style={{
              maxWidth: "100%",
              maxHeight: window.innerWidth <= 768 ? 400 : 500,
              borderRadius: 8,
              background: "#000",
              objectFit: "contain",
            }}
          />
        </Box>
      );
    }
    return (
      <InnerImageZoom
        zoomScale={1.05}
        src={item.original}
        fullscreenOnMobile={true}
        zoomSrc={item.original}
        zoomType="hover"
        className="image-gallery-image"
        style={{
          maxHeight: window.innerWidth <= 768 ? 400 : 500,
          objectFit: "contain",
          width: "100%",
          height: "auto",
        }}
      />
    );
  };

  const renderThumbInner = (item) => (
    <div
      className="image-gallery-thumbnail-inner"
      style={{ position: "relative" }}
    >
      {item.isVideo ? (
        <>
          <video
            src={item.thumbnail}
            muted
            loop
            playsInline
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              filter: "brightness(0.7)",
              borderRadius: 4,
            }}
          />
          <svg
            viewBox="0 0 40 40"
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: window.innerWidth <= 768 ? 16 : 24,
              height: window.innerWidth <= 768 ? 16 : 24,
              opacity: 0.85,
              pointerEvents: "none",
            }}
          >
            <circle cx="20" cy="20" r="18" fill="#000" opacity="0.5" />
            <polygon points="16,12 30,20 16,28" fill="#fff" />
          </svg>
        </>
      ) : (
        <img
          src={item.thumbnail}
          alt={item.thumbnailAlt}
          className="image-gallery-thumbnail-image"
        />
      )}
    </div>
  );

  return (
    <Box sx={{ position: "relative" }}>
      <style>{customStyles}</style>

      {media.length > 3 && (
        <>
          <IconButton
            className="thumbnail-nav-button up-button"
            size="small"
            onClick={scrollThumbnailsUp}
            sx={{ boxShadow: 1 }}
          >
            <KeyboardArrowUpIcon fontSize="small" />
          </IconButton>

          <IconButton
            className="thumbnail-nav-button down-button"
            size="small"
            onClick={scrollThumbnailsDown}
            sx={{ boxShadow: 1 }}
          >
            <KeyboardArrowDownIcon fontSize="small" />
          </IconButton>
        </>
      )}

      <ImageGallery
        ref={galleryRef}
        items={images}
        showThumbnails={true}
        showFullscreenButton={true}
        showPlayButton={false}
        thumbnailPosition="left"
        infinite={true}
        onSlide={handleSlide}
        renderItem={renderItem}
        renderThumbInner={renderThumbInner}
        onThumbnailClick={handleThumbnailClick}
        slideDuration={300}
        showNav={true}
        disableThumbnailScroll={true} // Important: let us handle thumbnail scrolling
      />
    </Box>
  );
};

export default ProductCarousel;
